import { Star, User } from 'lucide-react';

const RatingsSection = ({ ratings }) => {
  if (!ratings || ratings.length === 0) return null;

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          <Star
            key={i}
            className={`w-4 h-4 ${i <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-500'}`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const averageRating = ratings.length > 0 ? (ratings.reduce((sum, r) => sum + r.rating, 0) / ratings.length).toFixed(1) : 0;

  return (
    <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl shadow-2xl p-6 backdrop-blur-xl border border-white/20 hover:border-blue-400/50 transition-all duration-500">
      <h2 className="text-2xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent mb-4 flex items-center gap-2">
        <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
        Ratings & Reviews
      </h2>

      {/* Average Rating Summary - Fixed at Top */}
      {ratings.length > 0 && (
        <div className="mb-4 pb-4 border-b border-white/10 flex items-center gap-4">
          <div className="flex flex-col items-center">
            <div className="text-3xl font-bold text-white">
              {averageRating}
            </div>
            <div className="text-xs text-gray-400">
              out of 5
            </div>
          </div>

          <div className="flex flex-col flex-1">
            <div className="flex gap-1 mb-1">
              {renderStars(Math.round(averageRating))}
            </div>
            <p className="text-xs text-gray-400">
              Based on {ratings.length} review{ratings.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      )}

      {/* Scrollable Ratings List */}
      <div className="space-y-4 overflow-y-auto" style={{ maxHeight: '80vh' }}>
        {ratings.map(rating => (
          <div
            key={rating.id}
            className="bg-white/5 border border-white/10 rounded-lg p-4 hover:border-blue-400/50 transition-all hover:bg-white/10"
          >
            {/* Header with Avatar and Info */}
            <div className="flex items-start gap-4 mb-3">
              {rating.ratedBy?.avatar ? (
                <img
                  src={rating.ratedBy.avatar}
                  alt={rating.ratedBy.fullName}
                  className="w-10 h-10 rounded-full object-cover border border-white/20"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center border border-white/20">
                  <User className="w-5 h-5 text-white" />
                </div>
              )}

              <div className="flex-1">
                <h3 className="text-sm font-bold text-white">
                  {rating.ratedBy?.fullName || 'Anonymous'}
                </h3>
                <p className="text-xs text-gray-400">
                  {formatDate(rating.createdAt)}
                </p>
              </div>

              {/* Rating Stars */}
              <div>{renderStars(rating.rating)}</div>
            </div>

            {/* Comments */}
            {rating.comments && (
              <p className="text-sm text-gray-300 leading-relaxed break-words overflow-hidden">
                {rating.comments}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default RatingsSection;
